import React from 'react';

import { MarkdownBody } from '../../../common/markdown';
import QuestionInput from './questionInput';
import { inFlightQuestions } from '../../domain/cellObjects';

/**
 * InputControl — a question, rendered where it was asked, and what it was answered with.
 *
 * It used to be a banner pinned above the transcript, which put the one thing needing an answer
 * outside the sequence that explains it: you read a question with no idea what preceded it, and the
 * record of it arriving sat somewhere below, saying the same thing again. It IS the body of the
 * `input_needed` record now — in time order, once.
 *
 * **It never offers a control.** Answering happens in one place, at the foot of the thread
 * (`cellInput`), built from the request the cell publishes. A record that also carried an edit box
 * put two on screen at once — the question, a field inside its own bubble, and another field below
 * it — which is what this looked like before.
 *
 * So this renders the question as the question, with whatever was chosen marked, whether it was
 * answered a second ago or last week. A question that has been answered used to fall back to the raw
 * prose the worker rendered, which is how a four-question prompt became a wall of text under a green
 * tick; it reads as exactly what it read as when it was open.
 *
 * Props:
 *   request  — the request this record raised, or the cell's own if it snapshotted none
 *   mode     — the cell's affordance mode, for a request that carries none of its own
 *   answered — the answer set this question was answered with, or null
 *   live     — this is the question the cell is waiting on right now
 *   draft    — the answer being composed, when this is the live question (`useAnswerDraft`)
 *   busy     — a post is in flight
 */

function structuredQuestions(request) {
  const questions = request && request.questions;
  return Array.isArray(questions) && questions.length > 0;
}


function answerFor(request, mode, questions, answered) {
  // A gate whose question offers NO options was never a decision — its single exit is implied, and
  // its label is routing. Showing it would put the word `answered` in the bubble where the person's
  // own words belong, which is what a one-exit gate did.
  if ((request.mode || mode) !== 'gate' || !questions[0] || questions[0].choices.length > 0) {
    return answered || [];
  }
  const labels = new Set((((request.questions || [])[0] || {}).options || [])
    .map(option => option && option.label));
  return (answered || []).map(entry => ({
    ...entry,
    answer: (entry.answer || []).filter(value => !labels.has(value)),
  }));
}

export default function InputControl({ request, mode, answered, live, draft, busy }) {
  if (!request) return null;
  const questions = inFlightQuestions(request);
  const answers = answerFor(request, mode, questions, answered);
  // The record's prose is the worker's rendering of the WHOLE prompt — the question and its options
  // enumerated inside it. Where the request carries the questions structurally, the rows below say
  // all of that properly, so printing the blob as well shows every option twice: once as a numbered
  // line of prose and again as the row it is. Only a request with no structured questions has
  // nothing better, and then the prose is all there is.
  const headed = structuredQuestions(request);

  const decided = !live;
  // **Rows, and what they are read off.** The draft is the one reading of what a request ASKS —
  // `inFlightQuestions` — and an APPROVAL is where that differs from the record's own `questions`:
  // it has none, and its two fixed choices are derived. Deciding the rows off `questions` here left
  // an approval with no way to approve.
  const rows = !decided && !!draft && !draft.lone;

  return (
    <div className="mc-input">
      {/* The worker's rendering of the WHOLE prompt — the question with its options enumerated
          inside it. Shown only where the request carries no structured questions to say it
          properly; where it does, printing the blob as well shows every option twice. */}
      {request.prompt && !headed && !rows && (
        <MarkdownBody text={request.prompt} className="mc-input-prompt" />
      )}
      {/* **An open question is answered HERE, where it was asked** — its text, its options, and the
          row for an answer of your own, in time order with everything that led to it. Only the
          commit button is pinned at the foot (`cellInput`), because only the commit button is small:
          two questions with five options each, pinned, pushed the entire thread off the top of the
          pane.
          A LONE free-text question is the exception, and it is where the field lives rather than
          where it does not: its whole control is one box, that box is small enough to pin, and the
          record says the question over it. */}
      {rows && draft.questions.map((question, index) => (
        <div key={index} className={index > 0 ? 'mt-3' : ''}>
          <QuestionInput question={question} headed busy={busy}
            picked={draft.picked[index] || []} typed={draft.typed[index] || ''}
            onPick={choice => draft.pick(index, choice)}
            onType={value => draft.type(index, value)} />
        </div>
      ))}
      {!decided && !rows && headed && questions.map((question, index) => (
        <MarkdownBody key={index} text={question.prompt}
          className={index > 0 ? 'mc-question mt-3' : 'mc-question'} />
      ))}
      {request.policy && (
        <div className="mc-input-policy">
          Stopped by policy rule <code>{request.policy.rule}</code> ({request.policy.effect})
          {request.policy.detail ? ` — ${request.policy.detail}` : ''}
        </div>
      )}
      {/* **What an answered question still needs to say, and what it does not.**
          A select answers IN PLACE: the list with the taken option highlighted among the ones it was
          taken from, which is a thing only this record can show — the `intent · answer` below
          carries the label alone, and a label without its list says nothing about what was passed
          over.
          A TEXT question has no list. Its read-only row is the answer and nothing else, and the
          answer is already the record directly beneath it, in the person's own bubble with their
          name on it. So the question keeps its text and stops there.
          UNLESS nobody answered it — then the row is the only thing that says so, and a bare
          question with no reply under it looks like one still waiting. */}
      {decided && questions.map((question, index) => (
        <div key={index} className={index > 0 ? 'mt-3' : ''}>
          {question.choices.length > 0 || !(answers[index] || {}).answer?.length
            ? (
              <QuestionInput question={question} headed={headed} readOnly
                answer={(answers[index] || {}).answer || []} />
            )
            : headed && <MarkdownBody text={question.prompt} className="mc-question" />}
        </div>
      ))}
    </div>
  );
}
